/**
 * Service for handling JWT token operations
 */
const jwtService = {

  cachedUserInfo: null,

  TOKEN_KEY: "auth_token",
  REFRESH_TOKEN_KEY: "refresh_token",

  setToken: (token) => {
    jwtService.initUserInfoCache(token);
    localStorage.setItem(jwtService.TOKEN_KEY, token);
  },

  setRefreshToken: (token) => {
    localStorage.setItem(jwtService.REFRESH_TOKEN_KEY, token);
  },

  getToken: () => {
    return localStorage.getItem(jwtService.TOKEN_KEY);
  },

  getRefreshToken: () => {
    return localStorage.getItem(jwtService.REFRESH_TOKEN_KEY);
  },

  removeTokens: () => {
    localStorage.removeItem(jwtService.TOKEN_KEY);
    localStorage.removeItem(jwtService.REFRESH_TOKEN_KEY);
    jwtService.clearUserInfoCache();
  },

  parseToken: (token) => {
    if (!token) return null;

    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(c => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      );

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Error parsing JWT token:", error);
      return null;
    }
  },

  isAuthenticated: () => {
    const token = jwtService.getToken();
    if (!token) return false;

    try {
      if (!jwtService.parseToken(token)) return false;
      return true;
    } catch (error) {
      return false;
    }
  },

  getUserInfo: () => {
    if (jwtService.cachedUserInfo) return jwtService.cachedUserInfo;

    const token = jwtService.getToken();
    if (!token) return null;

    const decodedToken = jwtService.parseToken(token);
    if (!decodedToken) return null;

    jwtService.cachedUserInfo = {
      id: decodedToken.id,
      username: decodedToken.sub,
      email: decodedToken.email,
      companyName: decodedToken.companyName,
      companyId: decodedToken.companyId,
      roles: decodedToken.roles || [],
      // enabledPages controls which nav items are visible for this client.
      // Falls back to null — the route guard treats null as "show all" (backward compat).
      enabledPages: decodedToken.enabledPages || null,
      // lockedPages: visible in nav but inaccessible — shown with lock icon to tempt upsell.
      // Empty array means nothing is locked (no restriction). Null treated same as [].
      lockedPages: decodedToken.lockedPages || [],
      exp: decodedToken.exp,
    };

    return jwtService.cachedUserInfo;
  },

  clearUserInfoCache: () => {
    jwtService.cachedUserInfo = null;
  },

  initUserInfoCache: (token) => {
    if (!token) return null;

    const decodedToken = jwtService.parseToken(token);
    if (!decodedToken) return null;

    jwtService.cachedUserInfo = {
      id: decodedToken.id,
      username: decodedToken.sub,
      email: decodedToken.email,
      companyName: decodedToken.companyName,
      companyId: decodedToken.companyId,
      roles: decodedToken.roles || [],
      enabledPages: decodedToken.enabledPages || null,
      lockedPages: decodedToken.lockedPages || [],
      exp: decodedToken.exp,
    };

    return jwtService.cachedUserInfo;
  },

  hasRole: (role) => {
    const userInfo = jwtService.getUserInfo();
    if (!userInfo || !userInfo.roles) return false;

    return userInfo.roles.includes(role);
  },
};

export default jwtService;
